// Shared voice actions for recipe-context commands (RecipeView, Kitchen View,
// and site-wide): copy to cookbook, ingredients → shopping list, meal plan.
import api from "@/lib/api";
import { okPing } from "@/lib/voice";
import { splitChained, parseOne, normaliseStem, formatQtyPhrase } from "@/utils/shoppingVoiceParser";

const DAY_MAP = { monday: "mon", tuesday: "tue", wednesday: "wed", thursday: "thu", friday: "fri", saturday: "sat", sunday: "sun",
  mon: "mon", tue: "tue", tues: "tue", wed: "wed", thu: "thu", thur: "thu", thurs: "thu", fri: "fri", sat: "sat", sun: "sun" };
const DAY_KEYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];
const DAY_NAMES = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const DAY_RX = "(monday|tuesday|wednesday|thursday|friday|saturday|sunday|mon|tues?|wed|thu(?:rs?)?|fri|sat|sun)";

// "copy this recipe to my family favourites cookbook" / "save this to quick and easy"
export const COPY_THIS_RE = /^(?:please\s+)?(?:copy|save|add|put)\s+(?:this|it|this\s+recipe|the\s+recipe)\s+(?:to|into|in)\s+(?!(?:my\s+|the\s+)?(?:shopping\s+list|list|meal\s+plan(?:ner)?)\b)(?:my\s+|the\s+|our\s+)?(.+?)(?:\s+cook\s?book)?$/;
// "add milk, eggs and bread to my shopping list" — free-text items, added in the background.
export const ADD_ITEM_SHOP_RE = /^(?:please\s+)?(?:(?:can|could|would)\s+you\s+)?(?:add|put|pop|stick|get)\s+(.+?)\s+(?:to|on|onto|in|into)\s+(?:my\s+|the\s+|our\s+)?(?:shopping|grocery)\s+list(?:\s+please)?$/;
const NOT_AN_ITEM_RE = /^(?:this|it|that|these|those|them|(?:all\s+)?(?:of\s+)?(?:the\s+|these\s+|this\s+recipe'?s?\s+|its\s+)?ingredients?|this\s+recipe|the\s+recipe)$/;

// "take milk off my shopping list" / "remove eggs and bread from the shopping list"
export const REMOVE_SHOP_RE = /^(?:please\s+)?(?:(?:can|could|would)\s+you\s+)?(?:take|remove|delete|cross|strike|get\s+rid\s+of)\s+(?:the\s+)?(.+?)\s+(?:off|from|out\s+of)\s+(?:of\s+)?(?:my\s+|the\s+|our\s+)?(?:shopping|grocery)\s+list(?:\s+please)?$/;
// "read my shopping list" / "what's on my shopping list"
export const READ_SHOP_RE = /^(?:please\s+)?(?:(?:can|could|would)\s+you\s+)?(?:read(?:\s+out)?|tell\s+me(?:\s+what(?:'?s|\s+is)\s+on)?|what(?:'?s|\s+is)\s+on|what\s+do\s+i\s+need\s+from|list)\s+(?:me\s+)?(?:my\s+|the\s+|our\s+)?(?:shopping|grocery)\s+list(?:\s+(?:out|aloud|out\s+loud))?(?:\s+please)?$/;

const AISLE_SPEECH = [
  ["produce", "Fresh produce"], ["meat", "Meat and fish"], ["dairy", "Dairy and eggs"], ["bakery", "Bakery"],
  ["frozen", "Frozen"], ["baking", "Baking"], ["spices", "Spices and seasoning"], ["pantry", "Pantry"],
  ["drinks", "Drinks"], ["other", "Other"],
];

async function readShoppingList(speak) {
  const { data } = await api.get("/shopping-list");
  const todo = (data.items || []).filter((i) => !i.checked);
  if (!todo.length) { speak("Your shopping list is empty. Nothing left to buy."); return; }
  const known = new Set(AISLE_SPEECH.map(([k]) => k));
  const parts = AISLE_SPEECH.map(([key, label]) => {
    const names = todo.filter((i) => (known.has(i.aisle) ? i.aisle : "other") === key)
      .map((i) => formatQtyPhrase(i.quantity, i.unit, i.display_name || i.normalised_name));
    return names.length ? `${label}: ${names.join(", ")}.` : null;
  }).filter(Boolean);
  speak(`You've got ${todo.length} thing${todo.length === 1 ? "" : "s"} to buy. ${parts.join(" ")}`);
}

// Closest match: same name (ignoring plurals) wins; otherwise items containing the word.
function matchShopItems(items, raw) {
  const stem = normaliseStem(raw);
  if (!stem || stem.length < 2) return [];
  const nm = (i) => normaliseStem(i.display_name || i.normalised_name);
  const exact = items.filter((i) => nm(i) === stem || normaliseStem(i.normalised_name) === stem);
  if (exact.length) return [exact.find((i) => !i.checked) || exact[0]];
  const esc = stem.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const word = new RegExp(`\\b${esc}`);
  const hits = items.filter((i) => word.test(nm(i)));
  const unchecked = hits.filter((i) => !i.checked);
  return unchecked.length ? unchecked : hits;
}

function removeShopFlow(subject, { speak, ask, onDone }) {
  (async () => {
    const { data } = await api.get("/shopping-list");
    const items = data.items || [];
    const removed = []; const missed = []; const unsure = [];
    for (const name of splitChained(subject).map((n) => n.trim()).filter(Boolean)) {
      const hits = matchShopItems(items, name);
      if (!hits.length) missed.push(name);
      else if (hits.length > 1) unsure.push({ name, hits });
      else { await api.delete(`/shopping-list/${hits[0].item_id}`); removed.push(hits[0].display_name); }
    }
    if (removed.length) okPing();
    const said = [
      removed.length ? `Removed ${removed.join(" and ")}.` : "",
      missed.length ? `I couldn't find ${missed.join(" or ")} on your list.` : "",
    ].filter(Boolean).join(" ");
    if (!unsure.length) { speak(said || "Done."); if (removed.length) onDone?.(); return; }
    const { name, hits } = unsure[0];
    const opts = hits.slice(0, 4).map((h) => h.display_name);
    speak(`${said ? `${said} ` : ""}I found ${opts.slice(0, -1).join(", ")} and ${opts[opts.length - 1]} for ${name}. Which one should I take off?`);
    ask((reply) => {
      if (/^(?:no|none|cancel|never\s*mind|leave\s+it|neither)\b/.test(reply)) { speak("Okay, I'll leave them."); return true; }
      const all = /\b(?:all|both|every|them\s+all)\b/.test(reply);
      const pick = all ? hits : hits.filter((h) => reply.includes((h.display_name || "").toLowerCase())
        || (h.display_name || "").toLowerCase().includes(reply.replace(/^(?:the\s+)/, "").trim()));
      if (pick.length !== 1 && !all) { speak(`Please say one of: ${opts.join(", ")}. Or say cancel.`); return "retry"; }
      Promise.all(pick.map((h) => api.delete(`/shopping-list/${h.item_id}`)))
        .then(() => { okPing(); speak(`Removed ${pick.map((h) => h.display_name).join(" and ")}.`); onDone?.(); })
        .catch(() => speak("Sorry, I couldn't remove that."));
      return true;
    });
  })().catch(() => speak("Sorry, I couldn't update your shopping list."));
}

// "add these ingredients to my shopping list"
export const ING_TO_SHOP_RE = /\b(?:add|put|send|copy)\s+(?:all\s+)?(?:of\s+)?(?:these|the|this\s+recipe'?s?|its|the\s+recipe'?s?)?\s*(?:ingredients?|items|this|it)\s+(?:to|on|onto|into)\s+(?:my\s+|the\s+)?shopping\s+list\b/;
// "add this to my meal plan for thursday lunch" / "add chicken stir fry to my meal plan for thursday"
export const MEAL_PLAN_RE = /^(?:please\s+)?(?:add|put|plan|schedule)\s+(.+?)\s+(?:to|on|in|into|for)\s+(?:my\s+|the\s+)?(?:meal\s*plan(?:ner)?|menu|plan)\b(.*)$/;

export function parseMealPlan(txt) {
  const m = txt.toLowerCase().trim().replace(/[.?!]+$/, "").match(MEAL_PLAN_RE);
  if (!m) return null;
  const what = m[1].replace(/^(?:the\s+)?recipe\s+/, "").trim();
  const rest = m[2] || "";
  const d = rest.match(new RegExp(`\\b${DAY_RX}\\b`));
  const meal = (rest.match(/\b(breakfast|lunch|dinner|supper|tea|snacks?)\b/) || [])[1];
  const week = /\bnext\s+week\b|\bnext\s+(?=\w*day\b)/.test(rest) ? "next" : /\bthis\s+week\b|\bthis\s+(?=\w*day\b)/.test(rest) ? "this" : null;
  return {
    name: /^(?:this|it|this\s+recipe|this\s+one)$/.test(what) ? null : what,
    day: d ? DAY_MAP[d[1]] : null,
    meal: meal === "supper" || meal === "tea" ? "dinner" : meal?.startsWith("snack") ? "snacks" : meal || "dinner",
    week,
  };
}

export const parseDayReply = (txt) => {
  const d = txt.toLowerCase().match(new RegExp(`\\b${DAY_RX}\\b`));
  return d ? DAY_MAP[d[1]] : null;
};
export const parseWeekReply = (txt) => {
  const t = txt.toLowerCase();
  if (/\bnext\b|\bfollowing\b/.test(t)) return "next";
  if (/\bthis\b|\bcoming\b|\bthat\s+one\b|\bfirst\b|^yes\b/.test(t)) return "this";
  const n = t.match(/\b(\d{1,2})(?:st|nd|rd|th)?\b/);
  return n ? { dayOfMonth: parseInt(n[1], 10) } : null;
};

const isoDate = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
export function mondayOf(week) {
  const now = new Date();
  const mon = new Date(now.getFullYear(), now.getMonth(), now.getDate() - ((now.getDay() + 6) % 7));
  if (week === "next") mon.setDate(mon.getDate() + 7);
  return mon;
}
export function dateFor(week, day) {
  const d = mondayOf(week);
  d.setDate(d.getDate() + DAY_KEYS.indexOf(day));
  return d;
}
export const sayDate = (d) => d.toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long" });
export const dayName = (k) => DAY_NAMES[DAY_KEYS.indexOf(k)];
// Resolve a "the 19th" reply against this/next week for the chosen day.
export function weekFromReply(reply, day) {
  if (reply === "this" || reply === "next") return reply;
  if (reply?.dayOfMonth) {
    if (dateFor("this", day).getDate() === reply.dayOfMonth) return "this";
    if (dateFor("next", day).getDate() === reply.dayOfMonth) return "next";
  }
  return null;
}

export async function addToMealPlan({ recipeId, day, meal, week }) {
  await api.put("/meal-plan/slot", {
    week_start: isoDate(mondayOf(week)), day, meal_type: meal, recipe_id: recipeId, servings: 4, note: "",
  });
}

const MEAL_ORDER = ["breakfast", "lunch", "dinner", "snacks"];
export const READ_PLAN_RE = /\bwhat(?:'s|\s+is|s)?\s+(?:on\s+)?(?:my\s+|the\s+)?(?:meal\s*plan(?:ner)?|menu)\b|\bread\s+(?:me\s+|out\s+)?(?:my\s+)?meal\s*plan\b|\bwhat\s+(?:am\s+i|are\s+we)\s+(?:having|cooking|eating)\b/;
export const PLAN_WEEK_RE = /\b(?:plan|fill)\s+(?:my|the|our)\s+(?:week|meal\s*plan|meals|dinners)\b(?:\s+(?:with|using)\s+(.+))?/;

const slotLabel = (sl) => sl.recipe_title || sl.note || "";
async function loadPlan(week) {
  const { data } = await api.get("/meal-plan", { params: { week_start: isoDate(mondayOf(week)) } });
  return data?.slots || [];
}

export async function readPlan(week, day, speak) {
  const slots = (await loadPlan(week)).filter((sl) => slotLabel(sl));
  const days = day ? [day] : DAY_KEYS;
  const parts = days.map((d) => {
    const meals = slots.filter((sl) => sl.day === d).sort((a, b) => MEAL_ORDER.indexOf(a.meal_type) - MEAL_ORDER.indexOf(b.meal_type));
    return meals.length ? `${dayName(d)}: ${meals.map((m) => `${m.meal_type}, ${slotLabel(m)}`).join("; ")}.` : null;
  }).filter(Boolean);
  const when = day ? `${week === "next" ? "next" : "this"} ${dayName(day)}` : `${week === "next" ? "next" : "this"} week`;
  speak(parts.length ? `Here's ${when}. ${parts.join(" ")}` : `Nothing is planned for ${when} yet.`);
}

export async function suggestWeek(theme, week, diets = []) {
  const t = (theme || "").toLowerCase();
  const f = { max: /\b(?:quick|fast|speedy)\b/.test(t) ? 30 : null, easy: /\b(?:easy|simple)\b/.test(t) };
  const term = t.replace(/\b(?:quick|fast|speedy|easy|simple|and|some|dinners?|meals?|recipes?|suppers?|ideas?)\b/g, " ").replace(/\s+/g, " ").trim();
  const params = term.length >= 3 ? { q: term, limit: 60 } : { q: "ideas", limit: 60, browse: true, diet: diets.join(",") };
  const { data } = await api.get("/search", { params });
  const slots = await loadPlan(week);
  const used = new Set(slots.map((sl) => sl.recipe_id).filter(Boolean));
  const pool = (data.groups?.recipes || [])
    .filter((r) => !used.has(r.id) && (!f.max || (r.total_minutes && r.total_minutes <= f.max)) && (!f.easy || r.difficulty === "Easy")
      && diets.every((d) => (r.diet_badges || []).includes(d)))
    .sort(() => Math.random() - 0.5);
  const emptyDays = DAY_KEYS.filter((d) => !slots.some((sl) => sl.day === d && sl.meal_type === "dinner" && slotLabel(sl)));
  const picks = {};
  emptyDays.forEach((d) => { const r = pool.shift(); if (r) picks[d] = r; });
  return { picks, pool, emptyDays };
}

function planWeekFlow(theme, week, { speak, ask, onDone, diets = [] }) {
  suggestWeek(theme, week, diets).then(({ picks, pool, emptyDays }) => {
    const days = Object.keys(picks);
    if (!emptyDays.length) { speak(`Every dinner ${week === "next" ? "next" : "this"} week is already planned.`); return; }
    if (!days.length) { speak(`I couldn't find recipes for ${theme || "those dinners"}. Try another idea.`); return; }
    const readOut = () => days.map((d) => `${dayName(d)}, ${picks[d].title}`).join(". ");
    speak(`Here's my plan for the empty dinners${diets.length ? ", keeping to your diet" : ""}: ${readOut()}. Shall I save these? Say yes, no, or swap a day, like "swap Tuesday".`);
    ask((reply) => {
      const sw = reply.match(/\b(?:swap|change|replace)\b/) && parseDayReply(reply);
      if (sw && picks[sw]) {
        const r = pool.shift();
        if (!r) { speak(`I've run out of other ideas for ${dayName(sw)}. Say yes to save or no to cancel.`); return "retry"; }
        picks[sw] = r;
        speak(`${dayName(sw)} is now ${r.title}. Say yes to save, no to cancel, or swap another day.`);
        return "retry";
      }
      if (/^(?:yes|yeah|yep|sure|ok(?:ay)?|keep\s+them|save(?:\s+them)?|do\s+it|perfect|lovely)\b/.test(reply)) {
        Promise.all(days.map((d) => addToMealPlan({ recipeId: picks[d].id, day: d, meal: "dinner", week })))
          .then(() => { speak(`Saved ${days.length} dinners to your meal plan.`); onDone?.(); })
          .catch((e) => speak(e?.response?.data?.detail || "Sorry, I couldn't save the plan."));
        return true;
      }
      if (/^(?:no|nope|cancel|discard|never\s*mind|don'?t)\b/.test(reply)) { speak("Okay, I won't save them."); return true; }
      speak(`Say yes to save, no to cancel, or swap a day.`);
      return "retry";
    });
  }).catch(() => speak("Sorry, I couldn't plan your week just now."));
}

const askThisOrNext = (speak, ask, then) => {
  speak("This week or next week?");
  ask((reply) => {
    const w = /\bnext\b/.test(reply) ? "next" : /\bthis\b|\bcurrent\b/.test(reply) ? "this" : null;
    if (!w) { speak('Please say "this week" or "next week".'); return "retry"; }
    then(w); return true;
  });
};

export async function findRecipeByName(name) {
  const { data } = await api.get("/search", { params: { q: name, limit: 5 } });
  // Directory/own recipes first, then the Historical collection.
  return (data.groups?.recipes || [])[0] || (data.groups?.historical || [])[0] || null;
}

export async function copyToCookbook(recipeId, cookbookName, create = false) {
  const { data } = await api.post("/search/add-to-cookbook", { cookbook_name: cookbookName, create, recipe_ids: [recipeId] });
  return data;
}

export async function addIngredientsToShopping(recipe) {
  const { data } = await api.post("/shopping-list/add-from-recipe", {
    recipe_id: recipe.recipe_id,
    recipe_title: recipe.title,
    servings_multiplier: 1,
    ingredients: (recipe.ingredients || []).filter((i) => i?.name).map((i) => ({
      name: i.name, quantity: i.quantity, unit: i.unit || "", notes: i.notes || "",
    })),
  });
  return data;
}

// "how many calories in this?" / "how much protein" / "nutrition info"
export const NUTRITION_RE = /\b(?:calories|calorie|kcal|nutrition(?:al)?|macros|how\s+(?:much|many)\s+(?:grams\s+of\s+)?(?:protein|carbs?|carbohydrates?|fats?|fibre|fiber|sugars?|salt|sodium)|is\s+(?:this|it)\s+high\s+in)\b/;
const NUT_SEARCHY_RE = /\b(?:find|show|search|recipes|ideas|give\s+me|low|high\s+protein\s+recipes)\b/;
const NUT_FULL_RE = /\b(?:full|all|complete|every|everything|breakdown|details?|facts|information|tell\s+me\s+more|more)\b/;
const gr = (v) => (Math.round(v * 10) / 10).toString();
const NUT_FIELDS = [
  { k: "kcal", re: /calor|kcal|energy/, say: (v) => `${Math.round(v)} calories` },
  { k: "protein_g", re: /protein/, say: (v) => `${gr(v)} grams of protein` },
  { k: "carbs_g", re: /carb/, say: (v) => `${gr(v)} grams of carbohydrate` },
  { k: "fat_g", re: /\bfats?\b|fatty/, say: (v) => `${gr(v)} grams of fat` },
  { k: "fibre_g", re: /fib(?:re|er)/, say: (v) => `${gr(v)} grams of fibre` },
  { k: "sugar_g", re: /sugar/, say: (v) => `${gr(v)} grams of sugar` },
  { k: "sodium_mg", re: /sodium|salt/, say: (v) => `${Math.round(v)} milligrams of sodium` },
];
const BASIC = ["kcal", "protein_g", "carbs_g", "fat_g"];
const joinAnd = (a) => (a.length > 1 ? `${a.slice(0, -1).join(", ")} and ${a[a.length - 1]}` : a[0] || "");

export function nutritionSentence(n, t, full = false) {
  const asked = NUT_FIELDS.filter((f) => f.re.test(t)).map((f) => f.k);
  const keys = full ? NUT_FIELDS.map((f) => f.k) : asked.length ? asked : BASIC;
  const parts = NUT_FIELDS.filter((f) => keys.includes(f.k) && typeof n[f.k] === "number").map((f) => f.say(n[f.k]));
  return { text: parts.length ? `Per serving, it has ${joinAnd(parts)}.` : "I don't have that figure for this recipe.", full: full || keys.length >= NUT_FIELDS.length };
}

function nutritionFlow(t, recipe, { speak, ask }) {
  const full0 = /\b(?:full|all|complete|details|facts|information|breakdown)\b/.test(t);
  api.post(`/voice/nutrition/${recipe.recipe_id}`).then(({ data }) => {
    const n = data?.nutrition;
    if (!n) {
      const why = { disabled: "Nutrition estimates are switched off on this site.", signin: "Please sign in so I can work out the nutrition for this recipe.", no_ingredients: "This recipe has no ingredients listed, so I can't work out its nutrition." }[data?.reason];
      speak(why || "Sorry, I couldn't work out the nutrition for this recipe just now.");
      return;
    }
    const { text, full } = nutritionSentence(n, t, full0);
    const est = n.source === "ai" ? " That's an estimate." : "";
    speak(`${text}${est}${full ? "" : ' Say "full details" for the rest.'}`);
    if (!full) ask((reply) => {
      if (!NUT_FULL_RE.test(reply) || reply.split(" ").length > 5) return false;
      speak(nutritionSentence(n, reply, true).text);
      return true;
    });
  }).catch(() => speak("Sorry, I couldn't get the nutrition for this recipe."));
}

// "what can I make with chicken and rice?" / "help me use up leftover ham"
const LEFTOVERS_RE = /\b(?:what\s+(?:can|could|should|shall)\s+(?:i|we)\s+(?:make|cook|do)|what\s+(?:to|do\s+i)\s+(?:make|cook)|use\s+up|something\s+(?:with|using)|recipes?\s+using|i\s+(?:have|'ve\s+got|got)\s+(?:some\s+)?leftover)\s+(?:with|using|from|out\s+of)?\s*(.+)$/;
export function parseLeftovers(t) {
  const m = t.match(LEFTOVERS_RE);
  if (!m) return null;
  const list = m[1].replace(/\b(?:tonight|today|for\s+(?:dinner|lunch|tea|supper|breakfast)|in\s+my\s+(?:fridge|cupboard)|that\s+i\s+have|i\s+have)\b/g, " ")
    .split(/\s*(?:,|&|\+|\band\b|\bplus\b|\bor\b)\s*/)
    .map((x) => x.replace(/\b(?:some|the|a|an|my|leftovers?|left\s+over)\b/g, " ").replace(/\s+/g, " ").trim())
    .filter((x) => x.length >= 2);
  return list.length ? list : null;
}
export async function findLeftovers(ingredients, diets = []) {
  const { data } = await api.post("/voice/leftovers", { ingredients, diets });
  return data;
}

/** Runs a recipe-context voice command. `ask(onReply)` captures the next
 * utterance; onReply returns true (done), "retry" (consumed, keep asking)
 * or false (not an answer — release to normal routing).
 * Returns true if the utterance was handled. */
export function runRecipeVoice(txt, { recipe, user, speak, ask, onDone }) {
  const t = txt.toLowerCase().trim().replace(/[.?!]+$/, "");
  if (!user && (COPY_THIS_RE.test(t) || ING_TO_SHOP_RE.test(t) || MEAL_PLAN_RE.test(t))) {
    speak("Please sign in first so I can do that for you.");
    return true;
  }
  const onShoppingPage = window.location.pathname.startsWith("/shopping");
  if (READ_SHOP_RE.test(t)) {
    if (!user) { speak("Please sign in first so I can read your shopping list."); return true; }
    readShoppingList(speak).catch(() => speak("Sorry, I couldn't load your shopping list."));
    return true;
  }
  const rm = !onShoppingPage && t.match(REMOVE_SHOP_RE);
  if (rm && !NOT_AN_ITEM_RE.test(rm[1].trim())) {
    if (!user) { speak("Please sign in first so I can update your shopping list."); return true; }
    removeShopFlow(rm[1], { speak, ask, onDone });
    return true;
  }
  const ai = t.match(ADD_ITEM_SHOP_RE);
  // On /shopping the page's own voice command adds and refreshes the visible list.
  if (ai && !NOT_AN_ITEM_RE.test(ai[1].trim()) && !window.location.pathname.startsWith("/shopping")) {
    if (!user) { speak("Please sign in first so I can add to your shopping list."); return true; }
    const items = splitChained(ai[1]).map(parseOne).filter((p) => p.name && p.name.length >= 2);
    if (!items.length) return false;
    (async () => {
      try {
        for (const p of items) await api.post("/shopping-list/add-manual", { name: p.name, quantity: p.qty, unit: p.unit });
        okPing();
        speak("Added");
        onDone?.();
      } catch (e) { speak(e?.response?.data?.detail || "Sorry, I couldn't add that to your shopping list."); }
    })();
    return true;
  }
  if (NUTRITION_RE.test(t) && !NUT_SEARCHY_RE.test(t)) {
    if (!recipe) { speak("Open a recipe first, then ask me how many calories it has."); return true; }
    nutritionFlow(t, recipe, { speak, ask });
    return true;
  }
  if (recipe && ING_TO_SHOP_RE.test(t)) {
    addIngredientsToShopping(recipe)
      .then((d) => { const n = (d?.added ?? 0) + (d?.merged ?? 0) || (recipe.ingredients || []).length; speak(`Added ${n} ingredient${n === 1 ? "" : "s"} from ${recipe.title} to your shopping list.`); onDone?.(); })
      .catch((e) => speak(e?.response?.data?.detail || "Sorry, I couldn't add those to your shopping list."));
    return true;
  }
  const cm = recipe && t.match(COPY_THIS_RE);
  if (cm) {
    const name = cm[1].trim();
    const go = (create) => copyToCookbook(recipe.recipe_id, name, create).then((d) => {
      if (d.status === "not_found") {
        speak(`I couldn't find a cookbook called ${name}. Shall I create it? Say yes or no.`);
        ask((reply) => {
          if (/^(?:yes|yeah|yep|sure|ok(?:ay)?|please|do it|create it|go on)\b/.test(reply)) { go(true); return true; }
          if (/^(?:no|nope|don'?t|cancel|never ?mind)\b/.test(reply)) { speak("Okay, I won't create it."); return true; }
          return false;
        });
        return;
      }
      speak(d.limit_hit ? `${d.limit_hit}` : `Copied ${recipe.title} to ${d.title}.`);
      onDone?.();
    }).catch((e) => speak(e?.response?.data?.detail || "Sorry, I couldn't copy that."));
    go(false);
    return true;
  }
  if (READ_PLAN_RE.test(t)) {
    if (!user) { speak("Please sign in first so I can read your meal plan."); return true; }
    const d = parseDayReply(t);
    const week = /\bnext\b/.test(t) ? "next" : "this";
    readPlan(week, d, speak).catch(() => speak("Sorry, I couldn't load your meal plan."));
    return true;
  }
  const pw = t.match(PLAN_WEEK_RE);
  if (pw) {
    if (!user) { speak("Please sign in first so I can plan your week."); return true; }
    const theme = (pw[1] || "").replace(/\b(?:this|next)\s+week\b/, "").trim();
    const week = /\bnext\s+week\b/.test(t) ? "next" : /\bthis\s+week\b/.test(t) ? "this" : null;
    const go = (w) => planWeekFlow(theme, w, { speak, ask, onDone, diets: user?.diet_profile || [] });
    if (week) go(week); else askThisOrNext(speak, ask, go);
    return true;
  }
  const mp = parseMealPlan(t);
  if (mp && (mp.name || recipe)) {
    (async () => {
      let target = recipe;
      if (mp.name) {
        const hit = await findRecipeByName(mp.name).catch(() => null);
        if (!hit) { speak(`I couldn't find a recipe called ${mp.name}.`); return; }
        target = { recipe_id: hit.id, title: hit.title };
      }
      const finish = async (day, week) => {
        try {
          await addToMealPlan({ recipeId: target.recipe_id, day, meal: mp.meal, week });
          speak(`Added ${target.title} to ${mp.meal} on ${sayDate(dateFor(week, day))}.`);
          onDone?.();
        } catch (e) { speak(e?.response?.data?.detail || "Sorry, I couldn't add that to your meal plan."); }
      };
      const askWeek = (day) => {
        speak(`Do you mean this ${dayName(day)}, ${sayDate(dateFor("this", day))}, or next ${dayName(day)}, ${sayDate(dateFor("next", day))}?`);
        ask((reply) => {
          const w = weekFromReply(parseWeekReply(reply), day);
          if (!w) { speak(`Please say "this ${dayName(day)}" or "next ${dayName(day)}".`); return "retry"; }
          finish(day, w); return true;
        });
      };
      const askDay = (week) => {
        speak(`Which day ${week === "next" ? "next week" : "this week"}?`);
        ask((reply) => {
          const d = parseDayReply(reply);
          if (!d) { speak("Please say a day, like Thursday."); return "retry"; }
          finish(d, week); return true;
        });
      };
      if (mp.day && mp.week) finish(mp.day, mp.week);
      else if (mp.day) askWeek(mp.day);
      else if (mp.week) askDay(mp.week);
      else {
        speak("Which day?");
        ask((reply) => { const d = parseDayReply(reply); if (!d) { speak("Please say a day, like Thursday."); return "retry"; } askWeek(d); return true; });
      }
    })();
    return true;
  }
  return false;
}
